import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import moment from 'moment'
import { Button, Icon, Segment, Table } from 'semantic-ui-react'
import LoadingSpinner from '../../LoadingSpinner'
import { LOAD_EXPERT_RECOMMENDATION_PERFORMANCE_QUERY } from '../../../graphql/admin'

const CATEGORY_LABELS = {
    'Very good fit': 'Very good fit',
    'Good fit': 'Good fit',
    Borderline: 'Borderline',
    'Not a fit': 'Not a fit',
    'Not enough context': 'No context',
    'Not Evaluated': 'Not Evaluated',
}

const formatRate = (value) => `${Number(value || 0).toFixed(2)}%`

const ZERO_CATEGORY = {
    total: 0,
    invitedCount: 0,
    acceptedCount: 0,
    acceptanceRate: 0,
    screeningEligibleCount: 0,
    screeningPassedCount: 0,
    screeningPassRate: 0,
}

const PerformanceTable = ({ categories }) => (
    <Table celled striped>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>Evaluation Category</Table.HeaderCell>
                <Table.HeaderCell>Total</Table.HeaderCell>
                <Table.HeaderCell>Invited</Table.HeaderCell>
                <Table.HeaderCell>Accepted</Table.HeaderCell>
                <Table.HeaderCell>Acceptance Rate</Table.HeaderCell>
                <Table.HeaderCell>Screening Eligible</Table.HeaderCell>
                <Table.HeaderCell>Screening Passed</Table.HeaderCell>
                <Table.HeaderCell>Screening Pass Rate</Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {categories.map((category) => (
                <Table.Row key={category.category}>
                    <Table.Cell>
                        {CATEGORY_LABELS[category.category] || category.category}
                    </Table.Cell>
                    <Table.Cell>{category.total}</Table.Cell>
                    <Table.Cell>{category.invitedCount}</Table.Cell>
                    <Table.Cell>{category.acceptedCount}</Table.Cell>
                    <Table.Cell>{formatRate(category.acceptanceRate)}</Table.Cell>
                    <Table.Cell>{category.screeningEligibleCount}</Table.Cell>
                    <Table.Cell>{category.screeningPassedCount}</Table.Cell>
                    <Table.Cell>{formatRate(category.screeningPassRate)}</Table.Cell>
                </Table.Row>
            ))}
        </Table.Body>
    </Table>
)

const ExpertRecommendationPerformance = () => {
    const [expandedProjectId, setExpandedProjectId] = useState(null)
    const { loading, data, error } = useQuery(
        LOAD_EXPERT_RECOMMENDATION_PERFORMANCE_QUERY
    )

    const categories = useMemo(
        () =>
            data?.adminPortal?.expertRecommendationPerformance?.categories || [],
        [data]
    )

    const projects = useMemo(() => {
        const byProject = new Map()

        categories.forEach((category) => {
            const categoryName = category.category
            ;(category.projects || []).forEach((project) => {
                const current = byProject.get(project.projectId) || {
                    projectId: project.projectId,
                    projectTitle: project.projectTitle,
                    updatedAt: project.updatedAt,
                    totalRecommended: 0,
                    categories: {},
                }

                current.totalRecommended += project.total || 0
                current.categories[categoryName] = {
                    category: categoryName,
                    total: project.total || 0,
                    invitedCount: project.invitedCount || 0,
                    acceptedCount: project.acceptedCount || 0,
                    acceptanceRate: project.acceptanceRate || 0,
                    screeningEligibleCount: project.screeningEligibleCount || 0,
                    screeningPassedCount: project.screeningPassedCount || 0,
                    screeningPassRate: project.screeningPassRate || 0,
                }

                byProject.set(project.projectId, current)
            })
        })

        return Array.from(byProject.values())
            .map((project) => ({
                ...project,
                categoryRows: categories.map((category) => {
                    const categoryName = category.category
                    return (
                        project.categories[categoryName] || {
                            category: categoryName,
                            ...ZERO_CATEGORY,
                        }
                    )
                }),
            }))
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    }, [categories])

    if (loading) return <LoadingSpinner />
    if (error) {
        return (
            <div>
                Error loading expert recommendation performance: {error.message}
            </div>
        )
    }

    return (
        <Segment style={{ padding: '2.5rem', width: '100%' }}>
            <h2>Expert Recommendation Performance</h2>

            <h3>All Projects (Total)</h3>
            <PerformanceTable categories={categories} />
            <div style={{ marginTop: '0.75rem', color: '#666', fontSize: '0.9rem' }}>
                <p style={{ marginTop: '0.75rem', marginBottom: '0.25rem' }}>
                    <strong>Accepted:</strong> Invited experts whose response status is{' '}
                    <em>Accepted</em>.
                </p>
                <p style={{ marginTop: 0, marginBottom: '0.25rem' }}>
                    <strong>Screening Eligible:</strong> Invited experts on projects that
                    include at least one knockout question.
                </p>
                <p style={{ marginTop: 0 }}>
                    <strong>Screening Passed:</strong> Screening-eligible experts who
                    answered <em>Yes</em> for every knockout question.
                </p>
            </div>

            <h3 style={{ marginTop: '2rem' }}>By Project</h3>
            <Table celled striped>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell width={1} />
                        <Table.HeaderCell>Project</Table.HeaderCell>
                        <Table.HeaderCell>Updated At</Table.HeaderCell>
                        <Table.HeaderCell>Total Invited</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {projects.map((project) => {
                        const isExpanded = expandedProjectId === project.projectId

                        return (
                            <React.Fragment key={project.projectId}>
                                <Table.Row>
                                    <Table.Cell collapsing>
                                        <Button
                                            compact
                                            icon
                                            basic
                                            size="mini"
                                            onClick={() =>
                                                setExpandedProjectId(
                                                    isExpanded
                                                        ? null
                                                        : project.projectId
                                                )
                                            }
                                        >
                                            <Icon
                                                name={
                                                    isExpanded
                                                        ? 'chevron down'
                                                        : 'chevron right'
                                                }
                                            />
                                        </Button>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {project.projectTitle}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {project.updatedAt
                                            ? moment(project.updatedAt).format(
                                                  'MM/DD/YY hh:mm a'
                                              )
                                            : 'null'}
                                    </Table.Cell>
                                    <Table.Cell>{project.totalRecommended}</Table.Cell>
                                </Table.Row>

                                {isExpanded && (
                                    <Table.Row>
                                        <Table.Cell colSpan={4}>
                                            <PerformanceTable
                                                categories={project.categoryRows}
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </React.Fragment>
                        )
                    })}
                </Table.Body>
            </Table>
        </Segment>
    )
}

export default ExpertRecommendationPerformance
